/**
 * Endpunkte des Chat-Verlaufs. Eine Unterhaltung hängt entweder an einer
 * Akte (/cases/{id}/conversations/…) oder ist fallfrei
 * (/messages/conversations/…); convBase wählt den passenden Pfad.
 */
import type { ChatAttachment, ChatConversation, ChatMessage } from './chatModel'
import type { DirectionInfo } from './directionClassify'
import { API_BASE_URL } from '@/config'
import { useAuthStore } from '@/stores/authStore'

export type RecipientRole = 'to' | 'cc' | 'bcc'

export function convBase (conv: Pick<ChatConversation, 'id' | 'idCase'>): string {
  return conv.idCase
    ? `${API_BASE_URL}/protected/cases/${conv.idCase}/conversations/${conv.id}`
    : `${API_BASE_URL}/protected/messages/conversations/${conv.id}`
}

async function request (url: string, init: RequestInit = {}): Promise<Response> {
  const res = await fetch(url, { credentials: 'include', ...init })
  if (res.status === 401) {
    useAuthStore().logout()
    throw new Error('Sitzung abgelaufen')
  }
  return res
}

/**
 * GET ohne globale Fehlerbehandlung: Ein 403 (z. B. fehlende Berechtigung für
 * Kontakte) darf hier nicht abmelden – nur 401 beendet die Sitzung.
 * Liefert null, wenn der Abruf scheitert.
 */
export async function getJsonQuietly<T> (path: string): Promise<T | null> {
  const res = await request(`${API_BASE_URL}${path}`)
  if (!res.ok) {
    return null
  }
  return res.json() as Promise<T>
}

/** Nachrichten einer Unterhaltung; markiert sie für Teilnehmer als gelesen. */
export async function fetchMessages (conv: ChatConversation): Promise<ChatMessage[]> {
  const res = await request(`${convBase(conv)}/messages`)
  if (!res.ok) {
    throw new Error(`Nachrichten konnten nicht geladen werden (HTTP ${res.status})`)
  }
  const data = await res.json()
  return Array.isArray(data) ? data : []
}

/**
 * Nachricht senden. recipientParticipantIds = "Antworten" an ausgewählte
 * Beteiligte; ohne Auswahl "Allen antworten" (Anforderung 2.2).
 */
export async function sendMessage (
  conv: ChatConversation,
  content: string,
  deferExternalSend: boolean,
  recipientParticipantIds: number[] | null = null,
): Promise<ChatMessage> {
  const res = await request(`${convBase(conv)}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      content,
      deferExternalSend,
      ...(recipientParticipantIds?.length ? { recipientParticipantIds } : {}),
    }),
  })
  if (!res.ok) {
    throw new Error('Senden fehlgeschlagen')
  }
  return res.json()
}

/** Lädt Anhänge hoch und liefert die Namen der fehlgeschlagenen Dateien. */
export async function uploadAttachments (conv: ChatConversation, messageId: number, files: File[]): Promise<string[]> {
  const failed: string[] = []
  for (const file of files) {
    const fd = new FormData()
    fd.append('file', file)
    try {
      const res = await request(`${convBase(conv)}/messages/${messageId}/attachments`, { method: 'POST', body: fd })
      if (!res.ok) {
        failed.push(file.name || 'Anhang')
      }
    } catch {
      failed.push(file.name || 'Anhang')
    }
  }
  return failed
}

/** Dokument einer Akte als Anhang (Kopie per Referenz, Zugriff prüft das Backend). */
export interface CaseDocumentRef {
  idCase: number
  idDocument: number
  name?: string
}

/**
 * Dokumente aus der Akte anhängen (Kundenfeedback 24.10.2026, IV.4: auch beim
 * Antworten, nicht nur vom PC). Liefert die Namen der fehlgeschlagenen.
 */
export async function attachCaseDocuments (conv: ChatConversation, messageId: number, docs: CaseDocumentRef[]): Promise<string[]> {
  const failed: string[] = []
  for (const doc of docs) {
    try {
      const res = await request(`${convBase(conv)}/messages/${messageId}/attachments/from-document`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idCase: Number(doc.idCase), idDocument: Number(doc.idDocument) }),
      })
      if (!res.ok) {
        failed.push(doc.name || 'Dokument')
      }
    } catch {
      failed.push(doc.name || 'Dokument')
    }
  }
  return failed
}

/** Versand an Externe nach dem Upload auslösen (Gegenstück zu deferExternalSend). */
export async function dispatchExternals (conv: ChatConversation, messageId: number): Promise<void> {
  const res = await request(`${convBase(conv)}/messages/${messageId}/dispatch-externals`, { method: 'POST' })
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`)
  }
}

/** Den Lesestand führen nur Teilnehmer; mitlesende Kollegen erhalten 409. */
export async function markUnread (conv: ChatConversation): Promise<'ok' | 'not-participant' | 'error'> {
  const res = await request(`${convBase(conv)}/unread`, { method: 'POST' })
  if (res.ok) {
    return 'ok'
  }
  return res.status === 409 ? 'not-participant' : 'error'
}

/** Weiteren Empfänger (An/Cc/Bcc) in eine bestehende Unterhaltung aufnehmen. */
export async function addRecipient (
  conv: ChatConversation,
  recipient: { email: string, name?: string, role: RecipientRole },
): Promise<void> {
  const res = await request(`${convBase(conv)}/participants/external`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(recipient),
  })
  if (!res.ok) {
    throw new Error(res.status === 400 ? 'Bitte eine gültige E-Mail-Adresse angeben' : 'Empfänger konnte nicht hinzugefügt werden')
  }
}

/** Anlage als Blob (für Download, Miniatur und Großansicht). */
export async function fetchAttachmentBlob (conv: ChatConversation, att: ChatAttachment): Promise<Blob> {
  const res = await request(`${convBase(conv)}/messages/${att.idMessage}/attachments/${att.id}`)
  if (!res.ok) {
    throw new Error('Anlage konnte nicht geladen werden')
  }
  return res.blob()
}

export async function downloadAttachment (conv: ChatConversation, att: ChatAttachment): Promise<void> {
  saveBlob(await fetchAttachmentBlob(conv, att), att.filename)
}

/** Nachricht als PDF (Von/An/Datum/Betreff + Inhalt) für "Als Anhang weiterleiten". */
export async function exportMessagePdf (messageId: number): Promise<File> {
  const res = await request(`${API_BASE_URL}/protected/messages/${messageId}/export-pdf`, { method: 'POST' })
  if (!res.ok) {
    throw new Error('PDF konnte nicht erstellt werden')
  }
  const filename = res.headers.get('X-Filename') || `Nachricht_${messageId}.pdf`
  return new File([await res.blob()], filename, { type: 'application/pdf' })
}

export function saveBlob (blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export interface ConversationPage {
  items: ChatConversation[]
  hasMore: boolean
  nextBefore: string
}

export interface ConversationPageQuery {
  limit: number
  before?: string
  q?: string
  emails?: string[]
  orgIds?: number[]
  unread?: boolean
  unassigned?: boolean
  /** Nur der Papierkorb des Nutzers (sonst wird er ausgeschlossen). */
  trashed?: boolean
  /** Einer Akte zugeordnete Unterhaltungen weglassen (Kundenfeedback 21.10.2026, I). */
  hideAssigned?: boolean
  /** Von/An-Filter: Absender- bzw. Empfängeradressen (Kundenfeedback 02.10.2026, II). */
  from?: string[]
  to?: string[]
}

/** Von/An-Parameter an eine Query hängen (gemeinsam für globale und Fall-Liste). */
export function appendPeopleParams (params: URLSearchParams, people: { from?: string[], to?: string[] }): void {
  for (const e of people.from || []) {
    params.append('from', e)
  }
  for (const e of people.to || []) {
    params.append('to', e)
  }
}

/** Unterhaltungen einer Akte, optional nach Von/An gefiltert (null, wenn der Abruf scheitert). */
export async function fetchCaseConversations (caseId: number, people: { from?: string[], to?: string[] } = {}): Promise<ChatConversation[] | null> {
  const params = new URLSearchParams()
  appendPeopleParams(params, people)
  const suffix = params.toString() ? `?${params}` : ''
  const data = await getJsonQuietly<ChatConversation[]>(`/protected/cases/${caseId}/conversations${suffix}`)
  return Array.isArray(data) ? data : null
}

export interface MessagePerson {
  email: string
  name: string
  kind: 'user' | 'external'
  count: number
}

/** Personen-Facette für die Von/An-Felder (leer, wenn der Abruf scheitert). */
export async function fetchPeople (query: { q?: string, limit?: number, caseId?: number | null }): Promise<MessagePerson[]> {
  const params = new URLSearchParams({ limit: String(query.limit || 50) })
  if (query.q) {
    params.set('q', query.q.slice(0, 100))
  }
  if (query.caseId) {
    params.set('idCase', String(query.caseId))
  }
  const data = await getJsonQuietly<MessagePerson[]>(`/protected/messages/people?${params}`)
  return Array.isArray(data) ? data : []
}

/** Eine Seite der globalen Unterhaltungsliste (null, wenn der Abruf scheitert). */
export async function fetchConversationPage (query: ConversationPageQuery): Promise<ConversationPage | null> {
  const params = new URLSearchParams({ limit: String(query.limit) })
  if (query.before) {
    params.set('before', query.before)
  }
  if (query.q) {
    params.set('q', query.q)
  }
  if (query.unread) {
    params.set('unread', '1')
  }
  if (query.unassigned) {
    params.set('unassigned', '1')
  }
  if (query.trashed) {
    params.set('trashed', '1')
  }
  if (query.hideAssigned) {
    params.set('assigned', '0')
  }
  for (const e of query.emails || []) {
    params.append('email', e)
  }
  for (const o of query.orgIds || []) {
    params.append('org', String(o))
  }
  appendPeopleParams(params, query)
  const data = await getJsonQuietly<ConversationPage>(`/protected/messages/conversations?${params}`)
  if (!data || !Array.isArray(data.items)) {
    return null
  }
  return data
}

/** Fallfreie Unterhaltung einer Akte zuordnen (Zentrale Korrespondenz, 2.5). */
export async function assignConversation (conv: ChatConversation, idCase: number): Promise<void> {
  const res = await request(`${API_BASE_URL}/protected/messages/conversations/${conv.id}/assign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idCase }),
  })
  if (!res.ok) {
    throw new Error(res.status === 403 ? 'Kein Zugriff auf diese Akte' : 'Zuordnung fehlgeschlagen')
  }
}

/**
 * Falsch abgelegte E-Mail-Unterhaltung in eine andere Akte verschieben
 * (Fehlermeldung Kanzlei Polz, 15.10.2026). Dokumente und Chronologie
 * ziehen mit um.
 */
export async function moveConversation (conv: ChatConversation, fromCase: number, toCase: number): Promise<void> {
  const res = await request(`${API_BASE_URL}/protected/cases/${fromCase}/conversations/${conv.id}/move`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idCase: toCase }),
  })
  if (!res.ok) {
    if (res.status === 403) {
      throw new Error('Kein Zugriff auf diese Akte')
    }
    if (res.status === 409) {
      throw new Error('Nur E-Mails können verschoben werden – oder die E-Mail wurde bereits verschoben.')
    }
    throw new Error('Verschieben fehlgeschlagen')
  }
}

/**
 * Papierkorb je Nutzer (Kundenfeedback 02.10.2026, III): Die Unterhaltung
 * verschwindet nur aus der eigenen Ansicht; Akte und Chronologie bleiben.
 */
export async function trashConversation (conv: Pick<ChatConversation, 'id'>): Promise<void> {
  await conversationStateRequest(conv, 'trash', 'In den Papierkorb verschieben fehlgeschlagen')
}

export async function restoreConversation (conv: Pick<ChatConversation, 'id'>): Promise<void> {
  await conversationStateRequest(conv, 'restore', 'Wiederherstellen fehlgeschlagen')
}

async function conversationStateRequest (conv: Pick<ChatConversation, 'id'>, action: 'trash' | 'restore', failure: string): Promise<void> {
  const res = await request(`${API_BASE_URL}/protected/messages/conversations/${conv.id}/${action}`, { method: 'POST' })
  if (!res.ok) {
    throw new Error(res.status === 403 ? 'Kein Zugriff auf diese Unterhaltung' : failure)
  }
}

export type MessageDirection = 'in' | 'out'

/**
 * Gegenseite ausdrücklich festlegen: bei „in" der Absender, bei „out" der
 * Empfänger – als vollständige Liste (An/Cc), damit sich die Empfänger einer
 * dokumentierten Outlook-Mail nachträglich ändern lassen (Fehlermeldung
 * Kanzlei Polz 16.10.2026).
 */
export interface DirectionCounterpart {
  counterpartEmail?: string
  counterpartName?: string
  recipients?: Array<{ email: string, name: string, role: 'to' | 'cc' }>
}

/**
 * Richtung einer E-Mail korrigieren: Eine aus Outlook weitergeleitete Mail
 * wird als „gesendet" dokumentiert – „in" ordnet sie als empfangen ein und
 * übernimmt den Absender aus dem weitergeleiteten Kopf. Mit `counterpart`
 * wird die Gegenseite ausdrücklich festgelegt (Dialog „Absender & Richtung").
 * Liefert die aktualisierte Nachricht.
 */
export async function setMessageDirection (messageId: number, direction: MessageDirection, counterpart: DirectionCounterpart = {}): Promise<ChatMessage> {
  const res = await request(`${API_BASE_URL}/protected/messages/${messageId}/direction`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ direction, ...counterpart }),
  })
  if (!res.ok) {
    if (res.status === 409) {
      throw new Error('Nur E-Mails lassen sich als empfangen oder gesendet einordnen')
    }
    if (res.status === 400) {
      throw new Error('Bitte eine gültige E-Mail-Adresse angeben')
    }
    throw new Error(res.status === 403 ? 'Kein Zugriff auf diese Nachricht' : 'Einordnung fehlgeschlagen')
  }
  return res.json()
}

/** Aktuelle Einordnung einer E-Mail samt Vorschlag (weitergeleiteter Absender, bisherige Gegenseite). */
export async function fetchDirectionInfo (messageId: number): Promise<DirectionInfo> {
  const res = await request(`${API_BASE_URL}/protected/messages/${messageId}/direction`)
  if (!res.ok) {
    throw new Error(res.status === 403 ? 'Kein Zugriff auf diese Nachricht' : 'Einordnung konnte nicht geladen werden')
  }
  const data = await res.json() as DirectionInfo
  return {
    ...data,
    counterparts: Array.isArray(data.counterparts) ? data.counterparts : [],
    inCase: Boolean(data.inCase),
    onlyMessage: Boolean(data.onlyMessage),
  }
}

/**
 * „Nicht zuordnen": fallfreie Unterhaltung bewusst ohne Akte führen bzw.
 * die Markierung zurücknehmen (Kundenfeedback 02.10.2026, erster Teil, IV).
 */
export async function setConversationNoCase (conv: Pick<ChatConversation, 'id'>, noCase: boolean): Promise<void> {
  const res = await request(`${API_BASE_URL}/protected/messages/conversations/${conv.id}/no-case`, { method: noCase ? 'POST' : 'DELETE' })
  if (res.ok) {
    return
  }
  if (res.status === 409) {
    throw new Error('Die Unterhaltung ist inzwischen einer Akte zugeordnet')
  }
  throw new Error(res.status === 403 ? 'Kein Zugriff auf diese Unterhaltung' : 'Markierung konnte nicht gespeichert werden')
}

/** Bildquelle eines Logos (in KARS hinterlegt bzw. über die Firmen-Domain). */
export function logoUrl (source: { type: 'organization', idOrganization: number } | { type: 'domain', domain: string }): string {
  return source.type === 'organization'
    ? `${API_BASE_URL}/protected/organizations/${source.idOrganization}/logo`
    : `https://www.google.com/s2/favicons?domain=${encodeURIComponent(source.domain)}&sz=128`
}
